import { useState, useRef, useCallback } from 'react'

interface AIDoctorAdviceProps {
  patientInfo: {
    name?: string
    age?: number
    gender?: string
    diseaseName?: string
    transplantCount?: number
  }
  soapData: {
    s: string
    o: string
    a: string
    p: string
  }
}

// AIドクターのアドバイス生成（シンプルなルールベース）
function generateAdvice(patientInfo: AIDoctorAdviceProps['patientInfo'], soap: AIDoctorAdviceProps['soapData']): string {
  const adviceParts: string[] = []
  
  // 挨拶
  adviceParts.push('AIドクターからの診察アドバイスです。')
  
  // 患者情報に基づくコメント
  if (patientInfo.diseaseName) {
    if (patientInfo.diseaseName.includes('潰瘍性大腸炎')) {
      adviceParts.push(`${patientInfo.diseaseName}の治療において、腸内フローラのバランスは非常に重要です。`)
      adviceParts.push('FMT治療による腸内細菌叢の改善が期待できます。')
    } else if (patientInfo.diseaseName.includes('自閉スペクトラム')) {
      adviceParts.push('腸脳相関の観点から、腸内環境の改善が行動や情緒の安定に寄与する可能性があります。')
      adviceParts.push('保護者の方と密に連携しながら、経過を観察することをお勧めします。')
    } else if (patientInfo.diseaseName.includes('過敏性腸症候群')) {
      adviceParts.push('過敏性腸症候群はストレスや食生活との関連が強いです。')
      adviceParts.push('腸内フローラ移植と併せて、生活習慣の改善も重要です。')
    } else if (patientInfo.diseaseName.includes('うつ病')) {
      adviceParts.push('腸内細菌がセロトニンなどの神経伝達物質の産生に関与することが知られています。')
      adviceParts.push('腸内環境の改善により、気分の安定が期待できます。')
    } else if (patientInfo.diseaseName.includes('アトピー') || patientInfo.diseaseName.includes('皮膚')) {
      adviceParts.push('腸内環境と皮膚の状態には密接な関係があります。')
      adviceParts.push('腸内フローラのバランスを整えることで、アレルギー反応の軽減が期待できます。')
    } else if (patientInfo.diseaseName.includes('便秘')) {
      adviceParts.push('便秘の改善には、腸内細菌のバランスが重要な役割を果たします。')
      adviceParts.push('十分な水分摂取と発酵食品の摂取も併せてお勧めします。')
    }
  }

  // SOAPデータに基づくコメント
  if (soap.s) {
    if (soap.s.includes('改善') || soap.s.includes('良く')) {
      adviceParts.push('患者さんの自覚症状に改善が見られているのは良い兆候です。')
    } else if (soap.s.includes('痛') || soap.s.includes('辛')) {
      adviceParts.push('患者さんの訴えに十分配慮し、症状の緩和を優先的に検討してください。')
    }
  }

  if (soap.o) {
    if (soap.o.includes('正常') || soap.o.includes('良好')) {
      adviceParts.push('客観的所見も良好な経過を示しています。')
    }
  }

  // 移植回数に基づくコメント
  if (patientInfo.transplantCount) {
    if (patientInfo.transplantCount >= 5) {
      adviceParts.push(`すでに${patientInfo.transplantCount}回の移植を実施されています。長期的な経過観察と、効果の評価が重要です。`)
    } else if (patientInfo.transplantCount <= 2) {
      adviceParts.push('治療の初期段階です。効果が現れるまでに数回の移植が必要な場合があります。')
    }
  }

  // 一般的なアドバイス
  adviceParts.push('引き続き経過観察を行い、必要に応じて治療計画の見直しをご検討ください。')
  
  return adviceParts.join(' ')
}

export default function AIDoctorAdvice({ patientInfo, soapData }: AIDoctorAdviceProps) {
  const [isGenerating, setIsGenerating] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [advice, setAdvice] = useState('')
  const [error, setError] = useState<string | null>(null)
  
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null)

  const generateAndSpeak = useCallback(async () => {
    setIsGenerating(true)
    setError(null)

    try {
      // アドバイスを生成
      const generatedAdvice = generateAdvice(patientInfo, soapData)
      setAdvice(generatedAdvice)

      // Web Speech APIで音声合成
      if ('speechSynthesis' in window) {
        // 既存の音声を停止
        window.speechSynthesis.cancel()

        const utterance = new SpeechSynthesisUtterance(generatedAdvice)
        utteranceRef.current = utterance
        
        // 日本語音声を探す
        const voices = window.speechSynthesis.getVoices()
        const japaneseVoice = voices.find(voice => voice.lang.includes('ja'))
        if (japaneseVoice) {
          utterance.voice = japaneseVoice
        }
        utterance.lang = 'ja-JP'
        utterance.rate = 1.0
        utterance.pitch = 1.0

        utterance.onstart = () => setIsPlaying(true)
        utterance.onend = () => setIsPlaying(false)
        utterance.onerror = () => {
          setError('音声の再生に失敗しました')
          setIsPlaying(false)
        }

        window.speechSynthesis.speak(utterance)
      } else {
        setError('お使いのブラウザは音声合成に対応していません')
      }

    } catch (err) {
      console.error('Error generating advice:', err)
      setError('アドバイスの生成中にエラーが発生しました')
    } finally {
      setIsGenerating(false)
    }
  }, [patientInfo, soapData])

  const stopPlaying = useCallback(() => {
    window.speechSynthesis.cancel()
    setIsPlaying(false)
  }, [])

  const replayAudio = useCallback(() => {
    if (advice && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel()
      
      const utterance = new SpeechSynthesisUtterance(advice)
      const voices = window.speechSynthesis.getVoices()
      const japaneseVoice = voices.find(voice => voice.lang.includes('ja'))
      if (japaneseVoice) {
        utterance.voice = japaneseVoice
      }
      utterance.lang = 'ja-JP'
      utterance.rate = 1.0
      utterance.pitch = 1.0

      utterance.onstart = () => setIsPlaying(true)
      utterance.onend = () => setIsPlaying(false)
      utterance.onerror = () => setIsPlaying(false)

      window.speechSynthesis.speak(utterance)
    }
  }, [advice])

  // 音声リストを事前に読み込み
  if ('speechSynthesis' in window) {
    window.speechSynthesis.getVoices()
  }

  return (
    <div className="ai-doctor">
      <div className="ai-doctor__header">
        <div className="ai-doctor__avatar">
          <span className="material-symbols-outlined">smart_toy</span>
        </div>
        <div className="ai-doctor__title">
          <h3>AIドクター</h3>
          <p>診断結果に基づくアドバイス</p>
        </div>
      </div>

      <div className="ai-doctor__content">
        {!advice && !isGenerating && (
          <div className="ai-doctor__placeholder">
            <p>診断内容を入力後、AIドクターにアドバイスを求めることができます。</p>
            <button
              type="button"
              onClick={generateAndSpeak}
              className="ai-doctor__button ai-doctor__button--primary"
              disabled={!soapData.s && !soapData.o && !soapData.a && !soapData.p}
            >
              <span className="material-symbols-outlined">psychology</span>
              アドバイスを求める
            </button>
          </div>
        )}

        {isGenerating && (
          <div className="ai-doctor__loading">
            <div className="ai-doctor__spinner"></div>
            <p>アドバイスを生成中...</p>
          </div>
        )}

        {error && (
          <div className="ai-doctor__error">
            <span className="material-symbols-outlined">error</span>
            {error}
          </div>
        )}

        {advice && !isGenerating && (
          <div className="ai-doctor__advice">
            <div className="ai-doctor__text">
              {advice}
            </div>
            <div className="ai-doctor__controls">
              {isPlaying ? (
                <button
                  type="button"
                  onClick={stopPlaying}
                  className="ai-doctor__control ai-doctor__control--stop"
                >
                  <span className="material-symbols-outlined">stop</span>
                  停止
                </button>
              ) : (
                <button
                  type="button"
                  onClick={replayAudio}
                  className="ai-doctor__control"
                >
                  <span className="material-symbols-outlined">replay</span>
                  再生
                </button>
              )}
              <button
                type="button"
                onClick={generateAndSpeak}
                className="ai-doctor__control ai-doctor__control--regenerate"
              >
                <span className="material-symbols-outlined">refresh</span>
                再生成
              </button>
            </div>
          </div>
        )}
      </div>

      {isPlaying && (
        <div className="ai-doctor__playing">
          <div className="ai-doctor__wave">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
          <span>再生中...</span>
        </div>
      )}

      <style>{`
        .ai-doctor {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: var(--border-radius-lg);
          padding: var(--spacing-lg);
          color: white;
        }

        .ai-doctor__header {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
          margin-bottom: var(--spacing-lg);
        }

        .ai-doctor__avatar {
          width: 48px;
          height: 48px;
          background-color: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ai-doctor__avatar .material-symbols-outlined {
          font-size: 28px;
        }

        .ai-doctor__title h3 {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 2px;
        }

        .ai-doctor__title p {
          font-size: 0.75rem;
          opacity: 0.8;
        }

        .ai-doctor__content {
          background-color: rgba(255, 255, 255, 0.1);
          border-radius: var(--border-radius-md);
          padding: var(--spacing-md);
        }

        .ai-doctor__placeholder {
          text-align: center;
          padding: var(--spacing-md);
        }

        .ai-doctor__placeholder p {
          font-size: 0.875rem;
          opacity: 0.9;
          margin-bottom: var(--spacing-md);
        }

        .ai-doctor__button {
          display: inline-flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-sm) var(--spacing-lg);
          border: none;
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .ai-doctor__button--primary {
          background-color: white;
          color: #667eea;
        }

        .ai-doctor__button--primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        .ai-doctor__button--primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .ai-doctor__loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: var(--spacing-lg);
        }

        .ai-doctor__spinner {
          width: 32px;
          height: 32px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: var(--spacing-sm);
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .ai-doctor__loading p {
          font-size: 0.875rem;
          opacity: 0.9;
        }

        .ai-doctor__error {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-md);
          background-color: rgba(239, 68, 68, 0.2);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
        }

        .ai-doctor__advice {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-md);
        }

        .ai-doctor__text {
          font-size: 0.875rem;
          line-height: 1.8;
        }

        .ai-doctor__controls {
          display: flex;
          gap: var(--spacing-sm);
          justify-content: flex-end;
        }

        .ai-doctor__control {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          padding: var(--spacing-xs) var(--spacing-md);
          background-color: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: var(--border-radius-sm);
          color: white;
          font-size: 0.75rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .ai-doctor__control:hover {
          background-color: rgba(255, 255, 255, 0.3);
        }

        .ai-doctor__control--stop {
          background-color: rgba(239, 68, 68, 0.5);
        }

        .ai-doctor__control--regenerate {
          background-color: rgba(255, 255, 255, 0.3);
        }

        .ai-doctor__control .material-symbols-outlined {
          font-size: 16px;
        }

        .ai-doctor__playing {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          margin-top: var(--spacing-md);
          padding: var(--spacing-sm) var(--spacing-md);
          background-color: rgba(255, 255, 255, 0.1);
          border-radius: var(--border-radius-md);
          font-size: 0.75rem;
        }

        .ai-doctor__wave {
          display: flex;
          align-items: center;
          gap: 3px;
          height: 20px;
        }

        .ai-doctor__wave span {
          width: 3px;
          background-color: white;
          border-radius: 2px;
          animation: wave 1s ease-in-out infinite;
        }

        .ai-doctor__wave span:nth-child(1) { animation-delay: 0s; height: 8px; }
        .ai-doctor__wave span:nth-child(2) { animation-delay: 0.1s; height: 12px; }
        .ai-doctor__wave span:nth-child(3) { animation-delay: 0.2s; height: 16px; }
        .ai-doctor__wave span:nth-child(4) { animation-delay: 0.3s; height: 12px; }
        .ai-doctor__wave span:nth-child(5) { animation-delay: 0.4s; height: 8px; }

        @keyframes wave {
          0%, 100% { transform: scaleY(1); }
          50% { transform: scaleY(1.5); }
        }
      `}</style>
    </div>
  )
}
